import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { ReactComponent as TrevexLogo } from "../../../assets/trevexLogo.svg";
import { getCountryByCode } from "../../../utils/Helper";
import { capitalize, toSentenceCaseCP } from "../../../global_functions/global";
import { Modal } from "../";
import Login from "../../../pages/public/Login";
import { Button } from "./Button";
import { cn } from "./cn";
import { CompanyStatusBadge, resolveStatus } from "./StatusBadge";
import { CompanyMark } from "./CompanyMark";
import { SectorIcon, ArrowUpRight } from "../../../assets/ui/icons";
import { useSubIndustryNames } from "./useSubIndustryNames";

/* The industry names to show on a card. */
export const industryNames = (company) => {
  const src = company?.industry;
  if (!src) return [];

  const asName = (row) =>
    typeof row === "string" ? row : row?.sub_industry || row?.industry || null;

  /* A list of industry rows. */
  if (Array.isArray(src)) return src.map(asName).filter(Boolean);

  /* One row carrying the sub-industry list — what the directory search returns. */
  if (Array.isArray(src.sub_industry)) return src.sub_industry.map(asName).filter(Boolean);

  /* One row whose sub-industry is a single value. */
  const only = asName(src);
  return only ? [only] : [];
};

/* The other shape: bare primary keys and no names at all. */
export const subIndustryNames = (company, lookup) => {
  const ids = company?.sub_industry;
  if (!Array.isArray(ids) || !lookup) return [];
  return ids.map((id) => lookup.get(String(id))).filter(Boolean);
};

/* What the card shows. The named rows win where a payload has them; the ids
   are the fallback for the payloads that do not. */
export const cardIndustries = (company, lookup) => {
  const named = industryNames(company);
  return named.length > 0 ? named : subIndustryNames(company, lookup);
};

/* Redesigned search result card. */
export const CompanyCard = ({
  company,
  /* The trailing action. Defaults to opening the public profile — what the
     search results do — and My Companies swaps it for Manage without a second
     card component. */
  actionLabel = "View profile",
  onAction,
  actionAriaLabel,
  /* The pill in the status slot. */
  badge,
  className = "",
}) => {
  const navigate = useNavigate();
  const [viewMoreModalOpen, setViewMoreModalOpen] = useState(false);
  const verifiedUser = localStorage.getItem("isLoggedIn") === "true";

  const companyKey = company?.companyID || company?.wb_win;
  const isMember = resolveStatus(company).member;
  const displayName = company?.name || company?.registered_company_name;
  const subIndustryLookup = useSubIndustryNames();
  const industries = cardIndustries(company, subIndustryLookup);
  const primaryIndustry = industries[0];
  const extraIndustries = industries.length > 1 ? industries.length - 1 : 0;

  const handleCompany = () => {
    ReactGA.event({
      category: "VIEW_MORE",
      action: "VIEW_MORE: Getting Complete View of Company Profile",
      label: `Getting Complete View of Company Profile: "${companyKey}"`,
    });
    localStorage.setItem("lastVisited", `/search/${companyKey}`);

    if (!verifiedUser) {
      setViewMoreModalOpen(true);
      return;
    }
    navigate(`/search/${companyKey}`);
  };

  const handleViewMoreModalClose = () => {
    localStorage.removeItem("lastVisited");
    setViewMoreModalOpen(false);
  };

  const locationLabel = [capitalize(company?.state), getCountryByCode(company?.country_code)]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      {viewMoreModalOpen && (
        <Modal modalHandle={handleViewMoreModalClose}>
          <div className="w-[449px] px-[49px] h-[538px] py-8 flex flex-col justify-between">
            <div className="w-full flex justify-center">
              <TrevexLogo />
            </div>
            <h1 className="text-[22px] font-semibold text-center">Explore {company?.name}</h1>
            <p className="text-[12px] text-[#787878] font-normal text-center">
              Log in or register now for <span className="font-bold">FREE</span> to claim business
              information.
            </p>
            <div>
              <Login modalLogin={true} />
            </div>
          </div>
        </Modal>
      )}

      {/* THE CARD is the target — the one and only one. */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open ${displayName}`}
        onClick={handleCompany}
        onKeyDown={(e) => {
          /* Enter and Space are what a button answers to. Space also scrolls
             the page by default, hence the preventDefault. */
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCompany();
          }
        }}
        className={cn(
          "flex items-start sm:items-center gap-4 flex-wrap sm:flex-nowrap rounded-xl border border-[var(--line)] bg-white p-3 sm:p-5",
          "cursor-pointer transition-colors hover:border-[var(--coral)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)] focus-visible:ring-offset-2",
          className
        )}
      >
        {/* Mark — initials, not the logo: the directory is largely unclaimed
            records with no logo on file, so a derived mark reads consistently. */}
        <div className="shrink-0">
          <CompanyMark company={company} member={isMember} />
        </div>

        {/* Identity */}
        <div className="flex-1 min-w-0 basis-[180px] sm:basis-auto">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Plain text. The name carried its own click, coral hover and
                underline; the CARD now owns all of that, so a second target
                inside it only competed with the surface it sits on. */}
            <span className="min-w-0 truncate text-left font-bold text-[16px] text-[var(--ink)] font-sans">
              {displayName}
            </span>
          </div>

          {primaryIndustry && (
            <div className="flex items-center gap-[7px] mt-1.5 text-[var(--ink-2)] min-w-0">
              <span className="text-[var(--muted)] shrink-0 flex">
                <SectorIcon />
              </span>
              <span className="text-[13px] truncate font-sans">
                {toSentenceCaseCP(primaryIndustry)}
              </span>
              {extraIndustries > 0 && (
                <span className="text-[12px] text-[var(--muted)] shrink-0 font-sans">
                  · +{extraIndustries} More
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-1.5 text-[12px] text-[var(--muted)] font-sans">
            {company?.country_code && (
              <img
                src={`https://flagsapi.com/${company.country_code}/flat/64.png`}
                alt=""
                aria-hidden="true"
                className="w-4 h-4 object-cover"
              />
            )}
            <span>{locationLabel}</span>
          </div>
        </div>

        {/* Status + action */}
        {/* The action is its OWN destination — on My Businesses "Manage" goes
            to /edit/:id, which is not where the card goes. Its clicks stop
            here rather than also opening the profile behind it. */}
        <div
          onClick={(e) => e.stopPropagation()}
          /* Enter/Space on the action button would otherwise bubble to the
             card's own key handler and open the profile as well. */
          onKeyDown={(e) => e.stopPropagation()}
          className="flex items-center gap-4 shrink-0 w-full sm:w-auto sm:ml-auto justify-between sm:justify-end pt-3 sm:pt-0 border-t sm:border-t-0 border-[var(--line)]"
        >
          {badge === undefined ? <CompanyStatusBadge company={company} /> : badge}
          <Button
            variant="coral"
            size="sm"
            onClick={onAction ? () => onAction(company) : handleCompany}
            aria-label={actionAriaLabel || `${actionLabel} for ${displayName}`}
          >
            {actionLabel} <ArrowUpRight />
          </Button>
        </div>
      </div>
    </>
  );
};

export default CompanyCard;
